import * as React from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';

import type {
  CampaignClient,
  CampaignDetailDto,
} from '@/api/campaign-client';
import { useCampaignClient } from '@/api/campaign-client-context';
import {
  campaignDetailKey,
  campaignsListKey,
} from '@/api/use-campaigns';
import { useRankClientEvolution } from '@/api/use-rank-client-evolution';
import {
  availableClientSirens,
  buildEvolutionMatrix,
  campaignIdsForClient,
  type CampaignDetailLike,
} from '@/lib/client-evolution';
import { formatEurOrDash, seniorityLabel } from '@/lib/rank-labels';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';

// =============================================================================
// rank-client-evolution-screen.tsx — Écran ADMIN « Référencement / Évolution client » (R25, PHASE R).
// =============================================================================
// SURFACE UI DISTINCTE (C3) : cet écran vit dans la ZONE ADMIN NOMINATIVE (Rang), JAMAIS greffé sur le
// panneau de comparaison ANONYME (percentiles K-anon). Il sert le PRIX RETENU NOMINATIF (TJM retenu,
// DEC-15/16) — vue admin, AUCUN K-anon (ADR D10).
//
// PARCOURS (DoD R3) : sélectionner un CLIENT (SIREN) → voir le TJM retenu nominatif d'un profil évoluer
// campagne après campagne (FIGÉES, chronologiques). Autres clients EXCLUS (C5). Cellule multi-retenu =
// MÉDIANE fournisseur-AGNOSTIQUE (DEC-16). Campagne non figée EXCLUE.
//
// RÉPARTITION DES RESPONSABILITÉS :
//   • Périmètre client (SIREN → campaignId) résolu 100 % FRONT (C5, invariant n°2 — le back est stateless) ;
//   • L'API compose le prix retenu nominatif + la médiane ; l'UI TRANSPOSE (profil × temps) et habille.
//
// WCAG 2.1 AA : <table>/<caption>/<th scope>, sélecteur <label> lié, états role="status"/"alert",
// tiret pour les trous (jamais « 0 € »).

export interface RankClientEvolutionScreenProps {
  /** Retour (porté par l'adaptateur routeur). */
  readonly onBack: () => void;
}

/** Hook interne : résout les détails de campagne (list + details) pour le périmètre client FRONT (C5). */
function useCampaignDetails(): {
  readonly details: readonly CampaignDetailLike[];
  readonly isPending: boolean;
  readonly isError: boolean;
} {
  const client: CampaignClient = useCampaignClient();

  const listQuery = useQuery({
    queryKey: campaignsListKey,
    queryFn: () => client.listCampaigns(),
  });

  const ids = (listQuery.data ?? []).map((c) => c.campaignId);
  const detailQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: campaignDetailKey(id),
      queryFn: () => client.getCampaign(id),
      enabled: listQuery.isSuccess,
    })),
  });

  const details: CampaignDetailLike[] = detailQueries
    .map((q) => q.data)
    .filter((d): d is CampaignDetailDto => d !== undefined)
    .map((d) => ({
      campaignId: d.campaignId,
      intitule: d.intitule,
      clientScopes: d.clientScopes,
    }));

  const isPending =
    listQuery.isPending ||
    (listQuery.isSuccess && detailQueries.some((q) => q.isPending));
  const isError =
    listQuery.isError || detailQueries.some((q) => q.isError);

  return { details, isPending, isError };
}

export function RankClientEvolutionScreen({
  onBack,
}: RankClientEvolutionScreenProps): React.JSX.Element {
  const { details, isPending, isError } = useCampaignDetails();
  const [selectedSiren, setSelectedSiren] = React.useState<string>('');

  const sirens = React.useMemo(
    () => availableClientSirens(details),
    [details],
  );

  // C5 — campaignId du CLIENT sélectionné (clientScopes INCLUT le SIREN). Autres clients EXCLUS.
  const campaignIds = React.useMemo(
    () => campaignIdsForClient(details, selectedSiren),
    [details, selectedSiren],
  );

  // Intitulés lisibles par campaignId (libellés de colonnes).
  const labels = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const d of details) {
      if (d.intitule !== undefined) {
        map.set(d.campaignId, d.intitule);
      }
    }
    return map;
  }, [details]);

  const evolutionQuery = useRankClientEvolution(campaignIds);

  // Matrice transposée (profil × temps) — colonnes chronologiques, lignes couples, trous en tiret.
  const matrix = React.useMemo(
    () => buildEvolutionMatrix(evolutionQuery.data?.campaigns ?? [], labels),
    [evolutionQuery.data?.campaigns, labels],
  );

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Référencement — Évolution par client
          </h1>
          <p className="text-sm text-muted-foreground">
            Évolution du prix retenu nominatif (TJM retenu) par profil, campagne
            après campagne figée. Vue admin nominative (aucune anonymisation).
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          Retour
        </button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Client</CardTitle>
          <CardDescription>
            Sélectionnez un client (SIREN) pour suivre l’évolution de ses prix
            retenus sur ses campagnes figées successives.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isPending ? (
            <p role="status" className="text-muted-foreground">
              Chargement des campagnes…
            </p>
          ) : isError ? (
            <p role="alert" className="text-destructive">
              Erreur de chargement des campagnes.
            </p>
          ) : sirens.length === 0 ? (
            <p role="status" className="text-muted-foreground">
              Aucun client (SIREN) disponible.
            </p>
          ) : (
            <div className="max-w-sm space-y-1.5">
              <Label htmlFor="evo-client-siren">Client (SIREN)</Label>
              <select
                id="evo-client-siren"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={selectedSiren}
                onChange={(e) => setSelectedSiren(e.target.value)}
              >
                <option value="">— Sélectionner un client —</option>
                {sirens.map((siren) => (
                  <option key={siren} value={siren}>
                    {siren}
                  </option>
                ))}
              </select>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedSiren !== '' ? (
        <EvolutionTableCard
          selectedSiren={selectedSiren}
          isPending={evolutionQuery.isPending && campaignIds.length > 0}
          isError={evolutionQuery.isError}
          matrix={matrix}
        />
      ) : null}
    </div>
  );
}

// --- Table transposée profil × temps ----------------------------------------

interface EvolutionTableCardProps {
  readonly selectedSiren: string;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly matrix: ReturnType<typeof buildEvolutionMatrix>;
}

function EvolutionTableCard({
  selectedSiren,
  isPending,
  isError,
  matrix,
}: EvolutionTableCardProps): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Évolution du prix retenu — client {selectedSiren}</CardTitle>
        <CardDescription>
          Une ligne par profil × niveau ; une colonne par campagne figée (ordre
          chronologique). Prix retenu nominatif ; cellule multi-retenu = médiane
          (fournisseur agnostique). Valeur absente : tiret, jamais « 0 € ».
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <p role="status" className="text-muted-foreground">
            Chargement de l’évolution…
          </p>
        ) : isError ? (
          <p role="alert" className="text-destructive">
            Erreur de chargement de l’évolution.
          </p>
        ) : matrix.columns.length === 0 ? (
          <p role="status" className="text-muted-foreground">
            Aucune campagne figée pour ce client.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="ds-table w-full border-collapse text-sm">
              <caption className="sr-only">
                Évolution du prix retenu nominatif par profil et par niveau, pour
                le client {selectedSiren}, sur ses campagnes figées successives
                (colonnes en ordre chronologique).
              </caption>
              <thead>
                <tr className="border-b border-border">
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    Profil
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    Niveau
                  </th>
                  {matrix.columns.map((col) => (
                    <th
                      key={col.campaignId}
                      scope="col"
                      className="px-3 py-2 text-left font-medium text-muted-foreground"
                    >
                      <span className="block">{col.label}</span>
                      <span className="block text-xs font-normal text-muted-foreground">
                        {formatFrozenAt(col.frozenAt)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((row) => (
                  <tr
                    key={`${row.profileCode}::${row.seniorityLevel}`}
                    className="border-b border-border"
                  >
                    <th
                      scope="row"
                      className="px-3 py-2 text-left align-top font-medium"
                    >
                      {row.profileCode}
                    </th>
                    <td className="px-3 py-2 align-top">
                      {seniorityLabel(row.seniorityLevel)}
                    </td>
                    {row.cells.map((cell, idx) => {
                      const col = matrix.columns[idx];
                      return (
                        <td
                          key={col?.campaignId ?? String(idx)}
                          className="px-3 py-2 align-top tabular-nums"
                        >
                          <span className="block">
                            {formatEurOrDash(cell.tjmRetenuMedianEur)}
                          </span>
                          {cell.present && cell.retainedCount === 1 ? (
                            <span className="block text-xs font-normal text-muted-foreground">
                              {cell.raisonSociale === '' ? '—' : cell.raisonSociale}
                            </span>
                          ) : cell.present && cell.retainedCount > 1 ? (
                            <span className="block text-xs font-normal text-muted-foreground">
                              médiane ({String(cell.retainedCount)} retenus)
                            </span>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Formate un instant de figement ISO en date lisible (fr-FR, jour). Fail-safe : renvoie l'ISO si invalide. */
function formatFrozenAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
